import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { message } from "antd";
import {
  getExcelData,
  getGreenhousePlotData,
} from "../../../../utils/api";

import { UserContext } from "../../../../context/UserContext";
import { showRecommendationFollowSpinachDashboard } from "../../../../consts/config.js";
import useSpinachDashboardTable from "./useSpinachDashboardTable";
import useSpinachDashboardForecast from "./useSpinachDashboardForecast";
import useSpinachDashboardGroups from "./useSpinachDashboardGroups";
import useSpinachDashboardActions from "./useSpinachDashboardActions";
import useSpinachDashboardAutoRefresh from "./useSpinachDashboardAutoRefresh";
import { filterVisiblePlotData } from "./utils";
import { applyCorrectedForecastForDisplay } from "../../../../utils/positiveTrendCorrection";

const useSpinachDashboardState = ({
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  siteData,
  setSiteData,
  currentSite,
  setCurrentSite,
  siteLoading,
  setSiteLoading,
  currentCrop,
  setCurrentCrop,
  cropLoading,
  setCropLoading,
  cropData,
  setCropData,
  searchParams,
  setSearchParams,
  settingDefaults,
  setSettingDefaults,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [siteMetadata, setSiteMetadata] = useState({});
  const [greenhouseData, setGreenhouseData] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const [selectedGreenhouses, setSelectedGreenhouses] = useState([]);

  // Graph / row data
  const [allRowGraphData, setAllRowGraphData] = useState({});
  const [allPlotDataLoading, setAllPlotDataLoading] = useState(false);

  // Excel raw data
  const [excelRawData, setExcelRawData] = useState(null);

  // Final summary forecast
  const [summaryForecastData, setSummaryForecastData] = useState({});

  const [greenHouseLabel, setGreenHouseLabel] = useState("Greenhouse");
  const [blockLabel, setBlockLabel] = useState("Block");
  const [latLong, setLatLong] = useState(null);
  const [gddBaseTemperature, setGddBaseTemperature] = useState(5);

  const [minimumIdealHarvestLeafSize, setMinimumIdealHarvestLeafSize] =
    useState(7.5);
  const [maximumIdealHarvestLeafSize, setMaximumIdealHarvestLeafSize] =
    useState(8);

  // State for input values
  const [minLeafSizeInput, setMinLeafSizeInput] = useState(7.5);
  const [maxLeafSizeInput, setMaxLeafSizeInput] = useState(8);
  const [savingLeafSize, setSavingLeafSize] = useState(false);

  // For your existing "mid-stage" percentage logic
  const [idealHarvestLeafSizePercentage, setIdealHarvestLeafSizePercentage] =
    useState(98);

  const [greenHouseTableLoading, setGreenHouseTableLoading] = useState(false);

  const [harvestDateEdits, setHarvestDateEdits] = useState({});
  // keep DatePicker open state per row in the parent
  const [openHarvestPickers, setOpenHarvestPickers] = useState({});

  const [yieldEdits, setYieldEdits] = useState({});
  const [weightEdits, setWeightEdits] = useState({});
  const [bulkSaving, setBulkSaving] = useState({});
  const [showYieldInput, setShowYieldInput] = useState({});
  const [showWeightInput, setShowWeightInput] = useState({});

  const [yieldKeyNameForTable, setYieldKeyNameForTable] = useState("Yield");
  const [weightKeyNameForTable, setWeightKeyNameForTable] = useState("Weight");
  const [showForecastWeightColumn, setShowForecastWeightColumn] =
    useState(false);

  const { userData } = useContext(UserContext);
  const [exportModalVisible, setExportModalVisible] = useState(false);

  const [buildProgress, setBuildProgress] = useState({ done: 0, total: 0 });
  const prevProgressDoneRef = useRef(0);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const pendingGhFetchesRef = useRef(new Set());
  const MAX_CONCURRENCY = 11;
  const BATCH_SIZE = 4;

  const sortedForecastsCacheRef = useRef(new Map());
  const rowGeometryCacheRef = useRef(new Map());
  const lastExcelFetchKeyRef = useRef(null);

  const selectedGreenhousesSet = useMemo(
    () => new Set(selectedGreenhouses || []),
    [selectedGreenhouses],
  );

  const greenhouseNameToId = useMemo(() => {
    const map = new Map();
    greenhouseData.forEach((item) => {
      if (item?.name && item?.id) {
        map.set(item.name, item.id);
      }
    });
    return map;
  }, [greenhouseData]);

  useEffect(() => {
    const cache = sortedForecastsCacheRef.current;
    const validKeys = new Set(Object.keys(summaryForecastData || {}));
    for (const key of cache.keys()) {
      if (!validKeys.has(key)) cache.delete(key);
    }
  }, [summaryForecastData]);

  useEffect(() => {
    rowGeometryCacheRef.current.clear();
  }, [siteMetadata]);

  const {
    prefetchLoading,
    prefetchScheduled,
    summaryBuildScheduled,
    buildSummaryForecast,
    fetchHarvestedData,
    createHarvestedMap,
    extractTableDataFromRawData,
    getAverageRowLevelData,
    getSortedFilteredDatesRange,
    memoizedCalcDiffRange,
    getTempSliceOrFetch,
    getForecastLeafSize,
  } = useSpinachDashboardForecast({
    currentOrg,
    currentSite,
    currentCrop,
    siteMetadata,
    greenhouseData,
    selectedGreenhouses,
    selectedGreenhousesSet,
    allRowGraphData,
    excelRawData,
    allPlotDataLoading,
    gddBaseTemperature,
    latLong,
    setSummaryForecastData,
    setGreenHouseTableLoading,
    setBuildProgress,
  });

  // OF-129: DISPLAY-only view of the forecast. Rows built with a negative trend
  // (for opted-in non-admins) carry a `corrected` payload; swap it in here for
  // the table/groups/cards. The RAW `summaryForecastData` is what stays bound to
  // the persistence path (useSpinachDashboardActions) so the backend is untouched.
  const displaySummaryForecastData = useMemo(
    () => applyCorrectedForecastForDisplay(summaryForecastData),
    [summaryForecastData],
  );

  const isLoading = prefetchLoading || allPlotDataLoading || dropdownLoading;
  const actionsBusy =
    orgLoading ||
    siteLoading ||
    cropLoading ||
    dropdownLoading ||
    allPlotDataLoading ||
    prefetchLoading ||
    greenHouseTableLoading ||
    prefetchScheduled ||
    summaryBuildScheduled;
  const actionsDisabled = !initialLoadComplete || actionsBusy;

  useEffect(() => {
    setInitialLoadComplete(false);
  }, [currentOrg?.id, currentSite?.id, currentCrop?.id]);

  useEffect(() => {
    if (!initialLoadComplete && !actionsBusy) {
      setInitialLoadComplete(true);
    }
  }, [actionsBusy, initialLoadComplete]);

  const hasPendingEdits = useCallback(
    (ghName) => {
      return (
        Object.keys(harvestDateEdits).some((k) => k.startsWith(`${ghName}-`)) ||
        Object.keys(yieldEdits).some((k) => k.startsWith(`${ghName}-`)) ||
        Object.keys(weightEdits).some((k) => k.startsWith(`${ghName}-`))
      );
    },
    [harvestDateEdits, yieldEdits, weightEdits],
  );

  const getRowSortNumber = (name) => {
    const m = String(name || "").match(/\d+/);
    return m ? parseInt(m[0], 10) : Number.POSITIVE_INFINITY;
  };

  const naturalRowNameSort = (aName, bName) => {
    const aNum = getRowSortNumber(aName);
    const bNum = getRowSortNumber(bName);
    if (aNum !== bNum) return aNum - bNum;
    return String(aName || "").localeCompare(String(bName || ""));
  };

  // ------------------------------------------------------------------
  // 1) Set latLong and read metadata from currentSite
  // ------------------------------------------------------------------
  useEffect(() => {
    if (currentSite) {
      const { lat, long } = currentSite;
      setLatLong(`${lat},${long}`);

      // GDD base
      setGddBaseTemperature(
        currentSite?.metadata?.[currentCrop.name]
          ?.GDD_BASE_TEMPERATURE_IN_CELSIUS || 5,
      );

      // Read min & max from site metadata
      const minLeafSize =
        currentSite?.metadata?.[currentCrop.name]
          ?.MIN_IDEAL_HARVEST_LEAF_SIZE || 7.5;
      const maxLeafSize =
        currentSite?.metadata?.[currentCrop.name]
          ?.MAX_IDEAL_HARVEST_LEAF_SIZE || 8;

      setMinimumIdealHarvestLeafSize(minLeafSize);
      setMaximumIdealHarvestLeafSize(maxLeafSize);

      // Update input values as well
      setMinLeafSizeInput(minLeafSize);
      setMaxLeafSizeInput(maxLeafSize);

      // Additional metadata
      setIdealHarvestLeafSizePercentage(
        currentSite?.metadata?.[currentCrop.name]
          ?.IDEAL_HARVEST_LEAF_SIZE_PERCENTAGE || 98,
      );
      setYieldKeyNameForTable(
        currentSite?.metadata?.[currentCrop.name]
          ?.FORECAST_YIELD_KEY_NAME_FOR_TABLE || "Yield",
      );

      setWeightKeyNameForTable(
        currentSite?.metadata?.[currentCrop.name]
          ?.FORECAST_WEIGHT_KEY_NAME_FOR_TABLE || "Weight",
      );
      setShowForecastWeightColumn(
        !!currentSite?.metadata?.[currentCrop.name]
          ?.SHOW_FORECAST_WEIGHT_COLUMN || false,
      );
    }
  }, [currentSite, currentCrop]);

  // ------------------------------------------------------------------
  // 2) Fetch greenhousePlotData
  // ------------------------------------------------------------------
  const chunkArray = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  const fetchGreenhouseData = async (greenhouseIds) => {
    if (!greenhouseIds?.length) return;

    // Filter out IDs already being fetched to avoid duplicate calls
    const idsToFetch = greenhouseIds.filter(
      (id) => !pendingGhFetchesRef.current.has(id),
    );
    if (!idsToFetch.length) {
      return;
    }

    idsToFetch.forEach((id) => pendingGhFetchesRef.current.add(id));

    setAllPlotDataLoading(true);
    try {
      const batches = chunkArray(idsToFetch, BATCH_SIZE);
      let next = 0;

      const runWorker = async () => {
        while (next < batches.length) {
          const myIndex = next++;
          const batch = batches[myIndex];

          const graphRes = await getGreenhousePlotData(
            currentOrg,
            currentSite.id,
            currentCrop.id,
            batch,
          );

          const allData = filterVisiblePlotData(graphRes?.data?.data || {});

          setAllRowGraphData((prev) => ({ ...prev, ...allData }));
        }
      };
      const workers = Array(Math.min(MAX_CONCURRENCY, batches.length))
        .fill(0)
        .map(runWorker);

      await Promise.all(workers);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Oops! Error fetching graph data!";
      messageApi.open({ type: "error", content: msg });
    } finally {
      idsToFetch.forEach((id) => pendingGhFetchesRef.current.delete(id));
      setAllPlotDataLoading(false);
    }
  };

  const {
    handleSaveAllData,
    handleBulkSave,
    handleSaveLeafSize,
  } = useSpinachDashboardActions({
    currentOrg,
    currentSite,
    currentCrop,
    greenHouseLabel,
    blockLabel,
    siteMetadata,
    greenhouseData,
    summaryForecastData,
    setSummaryForecastData,
    harvestDateEdits,
    setHarvestDateEdits,
    yieldEdits,
    setYieldEdits,
    weightEdits,
    setWeightEdits,
    showForecastWeightColumn,
    setShowYieldInput,
    setShowWeightInput,
    latLong,
    setBulkSaving,
    excelRawData,
    allRowGraphData,
    buildSummaryForecast,
    fetchGreenhouseData,
    messageApi,
    minLeafSizeInput,
    maxLeafSizeInput,
    setMinimumIdealHarvestLeafSize,
    setMaximumIdealHarvestLeafSize,
    setSavingLeafSize,
  });

  const { renderGreenhouseTable } = useSpinachDashboardTable({
    harvestDateEdits,
    setHarvestDateEdits,
    openHarvestPickers,
    setOpenHarvestPickers,
    yieldEdits,
    setYieldEdits,
    weightEdits,
    setWeightEdits,
    showYieldInput,
    setShowYieldInput,
    showWeightInput,
    setShowWeightInput,
    summaryForecastData: displaySummaryForecastData,
    sortedForecastsCacheRef,
    rowGeometryCacheRef,
    naturalRowNameSort,
    siteMetadata,
    blockLabel,
    greenHouseLabel,
    minimumIdealHarvestLeafSize,
    maximumIdealHarvestLeafSize,
    yieldKeyNameForTable,
    weightKeyNameForTable,
    showForecastWeightColumn,
    hasPendingEdits,
    handleBulkSave,
    bulkSaving,
  });

  const fetchNewGreenhouseData = async (ghNames) => {
    if (!ghNames?.length) return;

    const existingGreenhouseNames = Object.keys(allRowGraphData);
    const newGreenhouseNames = ghNames.filter(
      (name) => !existingGreenhouseNames.includes(name),
    );

    if (!newGreenhouseNames.length) return;

    const greenhouseIds = newGreenhouseNames
      .map((name) => greenhouseNameToId.get(name))
      .filter(Boolean);

    if (greenhouseIds.length) {
      await fetchGreenhouseData(greenhouseIds);
    }
  };

  useEffect(() => {
    if (!selectedGreenhouses) return;
    const t = setTimeout(() => {
      fetchNewGreenhouseData(selectedGreenhouses);
    }, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGreenhouses]);

  const {
    groupedGreenhouses,
    expandedGroups,
    setExpandedGroups,
    type1Count,
    type2Count,
    type3Count,
    renderExceedList,
    renderMidStageList,
    renderEarlyMidStageList,
    handleGroupChange,
    filteredByType,
    groupConfig,
    cardConfig,
  } = useSpinachDashboardGroups({
    summaryForecastData: displaySummaryForecastData,
    minimumIdealHarvestLeafSize,
    maximumIdealHarvestLeafSize,
    idealHarvestLeafSizePercentage,
    blockLabel,
    greenHouseLabel,
    selectedGreenhouses,
    selectedGreenhousesSet,
    fetchNewGreenhouseData,
  });

  // ------------------------------------------------------------------
  // 3) Fetch Excel Data
  // ------------------------------------------------------------------
  const fetchExcelData = async () => {
    try {
      const greenhouseNames = greenhouseData.map((g) => g.name);
      const response = await getExcelData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        null,
        greenhouseNames,
      );
      const rawData = response?.data?.data?.rows;
      setExcelRawData(rawData);
      return rawData;
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Oops! Error fetching table data!";
      messageApi.open({ type: "error", content: msg });
      return null;
    }
  };

  useEffect(() => {
    const { done, total } = buildProgress;
    if (!total) {
      messageApi.destroy("build-progress");
      prevProgressDoneRef.current = 0;
      return;
    }
    const monotonicDone = Math.max(prevProgressDoneRef.current, done);
    prevProgressDoneRef.current = monotonicDone;

    if (monotonicDone === total) {
      setBuildProgress({ done: 0, total: 0 });
      prevProgressDoneRef.current = 0;
    }
    messageApi.open({
      key: "build-progress",
      type: monotonicDone < total ? "loading" : "success",
      content: `Loading ${greenHouseLabel}s… ${monotonicDone}/${total}`,
      duration: monotonicDone < total ? 0 : 1.2,
    });
  }, [buildProgress, greenHouseLabel]);

  useEffect(() => {
    if (greenhouseData.length > 0) {
      const greenhouseNamesKey = greenhouseData
        .map((g) => g.name || "")
        .join("|");
      if (lastExcelFetchKeyRef.current === greenhouseNamesKey) return;
      lastExcelFetchKeyRef.current = greenhouseNamesKey;
      fetchExcelData();
    }
  }, [greenhouseData]);

  const { greenhouseMapVisible, openMapModal, closeMapModal } =
    useSpinachDashboardAutoRefresh({
      messageApi,
      siteMetadata,
      selectedGreenhouses,
      selectedGreenhousesSet,
      greenhouseData,
      fetchGreenhouseData,
      currentOrg,
      currentSite,
      currentCrop,
      setSiteMetadata,
      setAllPlotDataLoading,
      setExcelRawData,
      buildSummaryForecast,
      allRowGraphData,
      searchParams,
      setSearchParams,
    });


  return {
    contextHolder,
    sidebarOpen,
    setSidebarOpen,
    siteMetadata,
    setSiteMetadata,
    currentOrg,
    setCurrentOrg,
    orgData,
    setOrgData,
    orgLoading,
    setOrgLoading,
    currentSite,
    setCurrentSite,
    siteData,
    setSiteData,
    siteLoading,
    setSiteLoading,
    currentCrop,
    setCurrentCrop,
    cropData,
    setCropData,
    cropLoading,
    setCropLoading,
    greenhouseData,
    setGreenhouseData,
    setDropdownLoading,
    searchParams,
    setSearchParams,
    settingDefaults,
    setSettingDefaults,
    setBlockLabel,
    blockLabel,
    greenHouseLabel,
    setGreenHouseLabel,
    selectedGreenhouses,
    setSelectedGreenhouses,
    minLeafSizeInput,
    maxLeafSizeInput,
    setMinLeafSizeInput,
    setMaxLeafSizeInput,
    savingLeafSize,
    handleSaveLeafSize,
    minimumIdealHarvestLeafSize,
    maximumIdealHarvestLeafSize,
    actionsDisabled,
    openMapModal,
    handleSaveAllData,
    userData,
    expandedGroups,
    setExpandedGroups,
    type1Count,
    type2Count,
    type3Count,
    renderExceedList,
    renderMidStageList,
    renderEarlyMidStageList,
    handleGroupChange,
    filteredByType,
    renderGreenhouseTable,
    groupConfig,
    isLoading,
    greenHouseTableLoading,
    summaryForecastData: displaySummaryForecastData,
    showRecommendationFollowSpinachDashboard,
    greenhouseMapVisible,
    closeMapModal,
    groupedGreenhouses,
    cardConfig,
    showForecastWeightColumn,
    weightKeyNameForTable,
    yieldKeyNameForTable,
    exportModalVisible,
    setExportModalVisible,
    excelRawData,
    allRowGraphData,
    latLong,
    gddBaseTemperature,
    fetchHarvestedData,
    createHarvestedMap,
    extractTableDataFromRawData,
    getAverageRowLevelData,
    getSortedFilteredDatesRange,
    memoizedCalcDiffRange,
    getTempSliceOrFetch,
    getForecastLeafSize,
  };
};

export default useSpinachDashboardState;
